import { CopyOutlined, DeleteOutlined, EditOutlined, WarningFilled } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Input, Modal } from 'antd';
import CaregiverProfileModal from 'components/modals/CaregiverProfileModal';
import { colors, radius, shadows } from 'design';
import { updateConversationLastMessageIfMatch } from 'hooks/conversationListMutators';
import { conversationMessagesQueryKey, conversationsQueryKey } from 'hooks/useConversations';
import { useMessageContextMenu } from 'hooks/useMessageContextMenu';
import useSelectConversations from 'hooks/useSelectConversations';
import { useSwipeToReply } from 'hooks/useSwipeToReply';
import posthog from 'posthog-js';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { MessageList, MessageType } from 'react-chat-elements';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiPatientConversationsManager from 'services/api/ApiPatientConversationsManager';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { UserDTO } from 'services/api/models/UserDTO';
import { conversationActions } from 'store/conversations';
import { ConversationMetadata } from 'store/conversations/types';
import { selectCurrentUser } from 'store/users/selectors';
import MessageUtil, { ChatMessage } from '../../models/Message';
import ExampleQuestions from './ExampleQuestions';
import './MessagesList.css';

export type MessagesListProps = {
  messages: ConversationMessage[];
  showExampleQuestions?: boolean;
  /** When false, swipe-to-reply is disabled (e.g. read-only historic conversations). */
  swipeToReplyEnabled?: boolean;
  /** Message list index (0-based) of the active search match to scroll to and flash-highlight. */
  activeSearchMatchMessageIndex?: number | null;
};

export type MessagesListHandle = {
  scrollToMessageIndex: (messageIndex: number) => void;
};

function isValidAndSafeUrl(urlString: string): boolean {
  try {
    let urlToValidate = urlString;
    if (urlString.startsWith('www.')) {
      urlToValidate = `https://${urlString}`;
    }

    const url = new URL(urlToValidate);

    const allowedProtocols = ['http:', 'https:'];
    if (!allowedProtocols.includes(url.protocol)) {
      return false;
    }

    if (!url.hostname) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

function convertTextToLinksInElement(
  element: HTMLElement,
  isCaregiver: boolean,
  isFromOtherUser: boolean,
  t: (key: string, options?: any) => string,
): void {
  const originalText = element.textContent || '';
  if (!originalText) return;

  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
  const matches: Array<{ url: string; index: number; length: number }> = [];

  let match;
  while ((match = urlRegex.exec(originalText)) !== null) {
    matches.push({
      url: match[0],
      index: match.index,
      length: match[0].length,
    });
  }

  if (matches.length === 0) return;

  element.textContent = '';

  let lastIndex = 0;
  matches.forEach(({ url, index, length }) => {
    if (index > lastIndex) {
      element.appendChild(document.createTextNode(originalText.substring(lastIndex, index)));
    }

    let href = url;
    if (url.startsWith('www.')) {
      href = `https://${url}`;
    }

    if (isValidAndSafeUrl(url)) {
      const link = document.createElement('a');
      link.href = href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = url;

      if (isCaregiver && isFromOtherUser) {
        link.onclick = e => {
          e.preventDefault();
          Modal.confirm({
            title: t('chats.linkConfirmation.title'),
            content: t('chats.linkConfirmation.content', { url: href }),
            icon: <WarningFilled style={{ color: colors.dataViz.bad }} />,
            okButtonProps: {
              style: {
                backgroundColor: colors.brand.accent,
                borderColor: colors.brand.accent,
              },
            },
            okText: t('chats.linkConfirmation.open'),
            cancelText: t('common.cancel'),
            onOk: () => {
              window.open(href, '_blank', 'noopener,noreferrer');
            },
          });
        };
      }

      element.appendChild(link);
    } else {
      element.appendChild(document.createTextNode(url));
    }

    lastIndex = index + length;
  });

  if (lastIndex < originalText.length) {
    element.appendChild(document.createTextNode(originalText.substring(lastIndex)));
  }
}

const useStyles = createUseStyles({
  container: {
    flex: 1,
    backgroundColor: colors.surface.page,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  messagesList: {
    paddingTop: 20,
    paddingBottom: 20,

    '& .rce-container-mbox': {
      position: 'relative',
      minWidth: 'unset',
      width: 'auto',
    },

    '& .rce-mbox-right': {
      // `background`, not `backgroundColor`: `chat.me.background` is the
      // accent gradient now, and `background-color` can't hold one.
      background: colors.chat.me.background,
      color: colors.chat.me.text,
      // A generous, uniform rounding rather than the tail-corner mix — `xl`,
      // not the button's full `pill`, which read as over-rounded on a
      // multi-line bubble.
      //
      // `!important`: react-chat-elements' own stylesheet carries
      // `.rce-mbox.rce-mbox--clear-notch { border-radius: 5px }`, which ties
      // our selector's specificity (two classes each) and wins the cascade
      // because it loads after ours — the same kind of tie the button hover
      // rules below already fight with `!important`.
      borderRadius: `${radius.lg}px !important`,

      // react-chat-elements ships its own `.rce-mbox-text` colour, which wins
      // over the inherited value above (a rule on the element itself beats
      // inheritance regardless of specificity) — so the message text and time
      // need their own explicit override, not just the bubble's.
      '& .rce-mbox-text, & .rce-mbox-time': {
        color: colors.chat.me.text,
      },

      '& .rce-mbox-reply': {
        backgroundColor: colors.brand.primarySubtle,
        borderColor: colors.brand.primarySubtle,
        // Own text: the bubble's `color: white` above would otherwise inherit
        // onto this light chip and go unreadable.
        color: colors.text.primary,
        marginTop: 0,
      },
    },

    '& .message-sent-from-other-caregiver .rce-mbox': {
      backgroundColor: colors.surface.muted,
      color: colors.text.tertiary,
    },

    '& .rce-mbox': {
      maxWidth: '80%',
      whiteSpace: 'pre-line',
      color: colors.chat.them.text,
      // Same rounding as `.rce-mbox-right` above, `!important` for the same reason.
      borderRadius: `${radius.lg}px !important`,
      boxShadow: shadows.xs,
      padding: '14px 20px 12px',

      '& .rce-mbox-reply': {
        marginTop: 8,
        backgroundColor: colors.surface.sunken,
        borderColor: colors.border.subtle,
      },
    },

    '& .rce-mbox-text': {
      fontSize: '18px',

      '& a': {
        color: colors.text.link,
        textDecoration: 'underline',
        cursor: 'pointer',
        wordBreak: 'break-all',

        '&:hover': {
          color: colors.text.link,
        },

        '&:visited': {
          color: colors.text.link,
        },
      },
    },
  },
  messageContextMenu: {
    position: 'fixed',
    zIndex: 9999,
    backgroundColor: colors.surface.base,
    borderRadius: 8,
    boxShadow: shadows.md,
    padding: '4px 0',
    minWidth: 160,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 18px',
    cursor: 'pointer',
    fontSize: 15,
    '&:hover': {
      backgroundColor: colors.surface.page,
    },
  },
  menuItemDanger: {
    color: colors.dataViz.bad,
  },
});

function MessagesListInner(props: MessagesListProps, ref: React.ForwardedRef<MessagesListHandle>) {
  const { messages, showExampleQuestions, swipeToReplyEnabled = true, activeSearchMatchMessageIndex } = props;

  // Hooks
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const styles = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Selectors
  const currentUser = useSelector(selectCurrentUser);
  const { conversationMetadata, conversationId } = useSelectConversations();

  // State
  const [caregiverIdDisplayedInModal, setCaregiverIdDisplayedInModal] = useState(undefined as string | undefined);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editText, setEditText] = useState('');

  // Scroll management refs
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hasInitialScrolledRef = useRef(false);
  const previousMessageCountRef = useRef(0);
  const searchHighlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchHighlightMboxRef = useRef<HTMLElement | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Callback ref to capture the parent scroll container
  const setContainerRef = (element: HTMLDivElement | null) => {
    containerRef.current = element;
    if (element) {
      // Parent element (from ConversationPage) is the actual scroll container
      scrollContainerRef.current = element.parentElement as HTMLDivElement;
    }
  };

  // Context menu — gesture detection only; action logic lives below
  const {
    contextMenu,
    contextMenuRef,
    closeContextMenu,
    handleContextMenu,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useMessageContextMenu({ containerRef, messagesRef, currentUser: currentUser ?? null });

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: async ({ messageId, messageText }: { messageId: number; messageText: string }) => {
      if (!conversationId) throw new Error('No conversation');
      if (currentUser?.is_caregiver) {
        await ApiDoctorConversationsManager.editMessage(conversationId, messageId, messageText);
      } else {
        await ApiPatientConversationsManager.editMessage(conversationId, messageId, messageText);
      }
    },
    onSuccess: (_, { messageId, messageText }) => {
      const editedAt = new Date().toISOString();
      queryClient.setQueryData<ConversationMessage[]>(
        conversationMessagesQueryKey(conversationId ?? ''),
        prev =>
          prev?.map(m => (m.id === messageId ? { ...m, message_text: messageText, edited_at: editedAt } : m)) ?? prev,
      );
      if (conversationId) {
        queryClient.setQueryData(
          conversationsQueryKey,
          updateConversationLastMessageIfMatch(conversationId, messageId, {
            message_text: messageText,
            edited_at: editedAt,
          }),
        );
      }
      posthog.capture('chats.message_edited');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (messageId: number) => {
      if (!conversationId) throw new Error('No conversation');
      if (currentUser?.is_caregiver) {
        await ApiDoctorConversationsManager.deleteMessage(conversationId, messageId);
      } else {
        await ApiPatientConversationsManager.deleteMessage(conversationId, messageId);
      }
    },
    onSuccess: (_, messageId) => {
      queryClient.setQueryData<ConversationMessage[]>(
        conversationMessagesQueryKey(conversationId ?? ''),
        prev =>
          prev?.map(m => {
            if (m.id === messageId) return { ...m, is_deleted: true };
            if (m.replied_to_message?.id === messageId)
              return { ...m, replied_to_message: { ...m.replied_to_message, is_deleted: true } };
            return m;
          }) ?? prev,
      );
      if (conversationId) {
        queryClient.setQueryData(
          conversationsQueryKey,
          updateConversationLastMessageIfMatch(conversationId, messageId, {
            message_text: '',
            is_deleted: true,
          }),
        );
      }
      posthog.capture('chats.message_deleted');
    },
  });

  // Track which message ID is being edited (set when the modal opens)
  const editingMessageIdRef = useRef<number | null>(null);

  const handleEditClickWithRef = useCallback(() => {
    if (!contextMenu.message) return;
    editingMessageIdRef.current = contextMenu.message.id;
    setEditText(contextMenu.message.message_text);
    setEditModalVisible(true);
    closeContextMenu();
  }, [contextMenu.message, closeContextMenu]);

  function handleCopyClick() {
    if (!contextMenu.message) return;
    navigator.clipboard.writeText(contextMenu.message.message_text).catch(() => {});
    closeContextMenu();
  }

  function handleDeleteClick() {
    if (!contextMenu.message) return;
    const messageId = contextMenu.message.id;
    closeContextMenu();
    Modal.confirm({
      title: t('chats.deleteMessage.title'),
      content: t('chats.deleteMessage.content'),
      okText: t('chats.deleteMessage.confirm'),
      cancelText: t('common.cancel'),
      icon: <WarningFilled style={{ color: colors.dataViz.bad }} />,
      okButtonProps: {
        style: { backgroundColor: colors.dataViz.veryBad, borderColor: colors.dataViz.veryBad },
      },
      onOk: () => {
        deleteMutation.mutate(messageId);
      },
    });
  }

  function handleEditSubmit() {
    const messageId = editingMessageIdRef.current;
    if (!messageId) return;
    editMutation.mutate({ messageId, messageText: editText });
    setEditModalVisible(false);
  }

  useImperativeHandle(ref, () => ({
    scrollToMessageIndex: (messageIndex: number) => {
      const container = scrollContainerRef.current;
      if (!container || !containerRef.current) return;
      const boxes = containerRef.current.querySelectorAll('.rce-container-mbox');
      const messageBox = boxes[messageIndex] as HTMLElement | undefined;
      messageBox?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  }));

  /**
   * When the active search match index changes: scroll that bubble into view and run the same
   * brief brightness pulse used for reply-reference taps. Does not depend on `messages` identity
   * (parent rebuilds the array each render) so typing does not re-trigger the flash.
   */
  useEffect(() => {
    if (searchHighlightTimeoutRef.current) {
      clearTimeout(searchHighlightTimeoutRef.current);
      searchHighlightTimeoutRef.current = null;
    }
    if (lastSearchHighlightMboxRef.current) {
      lastSearchHighlightMboxRef.current.style.filter = 'brightness(1)';
      lastSearchHighlightMboxRef.current.style.transition = '';
      lastSearchHighlightMboxRef.current = null;
    }

    if (activeSearchMatchMessageIndex == null || activeSearchMatchMessageIndex < 0) {
      return;
    }

    const container = scrollContainerRef.current;
    if (!container || !containerRef.current) return;
    const boxes = containerRef.current.querySelectorAll('.rce-container-mbox');
    const messageBox = boxes[activeSearchMatchMessageIndex] as HTMLElement | undefined;
    messageBox?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const mbox = messageBox?.querySelector('.rce-mbox') as HTMLElement | undefined;
    if (!mbox) return;

    const originalMessage = messagesRef.current[activeSearchMatchMessageIndex];
    const isOurMessage = originalMessage?.sender?.id === currentUser?.id;

    lastSearchHighlightMboxRef.current = mbox;
    mbox.style.transition = 'filter 0.35s ease';
    mbox.style.filter = isOurMessage ? 'brightness(1.08)' : 'brightness(0.94)';

    searchHighlightTimeoutRef.current = setTimeout(() => {
      mbox.style.filter = 'brightness(1)';
      searchHighlightTimeoutRef.current = null;
      if (lastSearchHighlightMboxRef.current === mbox) {
        lastSearchHighlightMboxRef.current = null;
      }
    }, 700);
    // Intentionally only `activeSearchMatchMessageIndex`: parent passes a new `messages` array every render;
    // including it would re-flash on every keystroke while the match index stays the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [activeSearchMatchMessageIndex]);

  /** Clear any in-progress search highlight timer and reset filter styles on unmount. */
  useEffect(() => {
    return () => {
      if (searchHighlightTimeoutRef.current) {
        clearTimeout(searchHighlightTimeoutRef.current);
        searchHighlightTimeoutRef.current = null;
      }
      if (lastSearchHighlightMboxRef.current) {
        lastSearchHighlightMboxRef.current.style.filter = 'brightness(1)';
        lastSearchHighlightMboxRef.current.style.transition = '';
        lastSearchHighlightMboxRef.current = null;
      }
    };
  }, []);

  /**
   * After each render, patch the DOM of any deleted or edited messages
   * so the visual appearance stays in sync without requiring react-chat-elements
   * to expose those fields.
   */
  useEffect(() => {
    if (!containerRef.current) return;
    const boxes = containerRef.current.querySelectorAll('.rce-container-mbox');
    boxes.forEach((box, idx) => {
      const message = messagesRef.current[idx];
      if (!message) return;
      const textEl = box.querySelector('.rce-mbox-text');
      if (!textEl) return;

      if (message.is_deleted) {
        textEl.innerHTML = `<em style="color:${colors.text.placeholder};font-size:16px;">${t('chats.messageDeleted')}</em>`;
        // Remove existing "edited" label if present
        box.querySelector('.gg-edited-label')?.remove();
        return;
      }

      // Handle "Modifié" label for edited messages — placed inline before the
      // timestamp inside .rce-mbox-time so it appears on the same line.
      const existingLabel = box.querySelector('.gg-edited-label');
      if (message.edited_at) {
        if (!existingLabel) {
          const timeEl = box.querySelector('.rce-mbox-time');
          if (timeEl) {
            const label = document.createElement('span');
            label.className = 'gg-edited-label';
            label.style.cssText = `color:${colors.text.placeholder};order:-1;margin-right:8px;`;
            label.textContent = t('chats.messageEdited');
            timeEl.appendChild(label);
          }
        }
      } else {
        existingLabel?.remove();
      }
    });
  });

  /**
   * Scroll behavior:
   * 1. On first render with messages: Instantly scroll to bottom
   * 2. On new message: Smoothly scroll the last message into view (any scroll position)
   */
  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const container = scrollContainerRef.current;
    const messageCountIncreased = messages.length > previousMessageCountRef.current;
    const isFirstRender = !hasInitialScrolledRef.current && messages.length > 0;

    if (isFirstRender) {
      // Initial scroll to bottom - instant, no animation (GYN-225)
      const maxScroll = container.scrollHeight - container.clientHeight;
      container.scrollTop = maxScroll;
      hasInitialScrolledRef.current = true;
    } else if (messageCountIncreased) {
      // New message: after paint, smooth-scroll last bubble into view
      requestAnimationFrame(() => {
        const boxes = containerRef.current?.querySelectorAll('.rce-container-mbox');
        const lastBox = boxes?.[boxes.length - 1] as HTMLElement | undefined;
        lastBox?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    }

    previousMessageCountRef.current = messages.length;
  }, [messages]);

  function setReplyingToMessageByIndex(messageIndex: number) {
    const originalMessage = messages[messageIndex];

    if (originalMessage && conversationId) {
      dispatch(
        conversationActions.setReplyingToMessage({
          conversationId,
          replyingTo: {
            messageIndex,
            messageId: originalMessage.id,
            messageText: originalMessage.message_text,
            senderName: originalMessage.sender.display_name,
          },
        }),
      );
    }
  }

  useEffect(() => {
    const messageBoxContainers = document.getElementsByClassName('rce-container-mbox');
    const isCaregiver = currentUser?.is_caregiver || false;

    Array.from(messageBoxContainers).forEach(container => {
      const messageBox = container.querySelector('.rce-mbox');
      const textElement = container.querySelector('.rce-mbox-text') as HTMLElement;

      if (!textElement || textElement.querySelector('a')) {
        return;
      }

      const isFromOtherUser =
        messageBox?.classList.contains('rce-mbox') && !messageBox?.classList.contains('rce-mbox-right');

      convertTextToLinksInElement(textElement, isCaregiver, isFromOtherUser, t);
    });
  }, [messages, currentUser, t]);

  // Swipe-to-reply gesture handling (disabled in read-only views such as historic conversations)
  useSwipeToReply({
    messages,
    onReply: setReplyingToMessageByIndex,
    enabled: swipeToReplyEnabled,
  });

  function closeCaregiverProfileModal() {
    setCaregiverIdDisplayedInModal(undefined);
  }

  function handleReplyMessageClick(message: MessageType) {
    const chatMessage = message as ChatMessage;
    if (chatMessage.reply) {
      // Find the original message by its ID from the reply data
      const repliedMessageId = chatMessage.reply.id;
      const replyMessageIndex = messages.findIndex(m => m.id === repliedMessageId);

      if (replyMessageIndex >= 0) {
        const messageBoxes = document.getElementsByClassName('rce-container-mbox');
        const messageBox = messageBoxes[replyMessageIndex];

        if (messageBox) {
          // Add data attribute to the message box for easier identification
          (messageBox as HTMLElement).setAttribute('data-message-id', String(repliedMessageId));

          messageBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const mbox = messageBox.querySelector('.rce-mbox');

          if (mbox) {
            const originalMessage = messages[replyMessageIndex];
            const isOurMessage = originalMessage.sender.id === currentUser?.id;

            const element = mbox as HTMLElement;
            element.style.transition = 'filter 0.35s ease';
            element.style.filter = 'brightness(1)';

            // Apply subtle highlight: brighter for our messages, darker for others
            element.style.filter = isOurMessage ? 'brightness(1.08)' : 'brightness(0.94)';

            // Fade back to normal
            setTimeout(() => {
              element.style.filter = 'brightness(1)';
            }, 700);
          }
        }
      }
    }
  }

  // Replace deleted message text so react-chat-elements renders the placeholder bubble
  const displayedMessages = messages.map(m => (m.is_deleted ? { ...m, message_text: t('chats.messageDeleted') } : m));

  return (
    <div
      ref={setContainerRef}
      className={styles.container}
      data-swipe-to-reply={swipeToReplyEnabled ? 'true' : 'false'}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {caregiverIdDisplayedInModal && (
        <CaregiverProfileModal
          caregiverId={caregiverIdDisplayedInModal}
          open
          onClose={() => closeCaregiverProfileModal()}
        />
      )}
      <MessageList
        referance={messageListRef}
        className={styles.messagesList}
        dataSource={
          MessageUtil.convertMessagesToComponentDataType(
            displayedMessages,
            currentUser as UserDTO,
            conversationMetadata as ConversationMetadata,
          ) as MessageType[]
        }
        lockable={false}
        toBottomHeight="100%"
        messageBoxStyles={{ color: 'rgba(46, 46, 46, 0.88)', lineHeight: '22px' }}
        onTitleClick={messageDetails => {
          if (
            (messageDetails as ChatMessage).sender?.is_caregiver &&
            (messageDetails as ChatMessage).sender?.id &&
            !currentUser?.is_caregiver
          ) {
            setCaregiverIdDisplayedInModal((messageDetails as ChatMessage).sender?.id);
          }
        }}
        onReplyMessageClick={handleReplyMessageClick}
      />
      {showExampleQuestions && <ExampleQuestions />}

      {/* Floating context menu for copy/edit/delete */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className={styles.messageContextMenu}
          style={
            contextMenu.right !== undefined
              ? { right: contextMenu.right, top: contextMenu.y }
              : { left: contextMenu.x, top: contextMenu.y }
          }
          onClick={e => e.stopPropagation()}
        >
          <div className={styles.menuItem} onClick={handleCopyClick}>
            <CopyOutlined />
            {t('chats.copyMessage')}
          </div>
          {/* Edit and delete are only available for the current user's messages */}
          {contextMenu.message?.sender.id === currentUser?.id && (
            <>
              <div className={styles.menuItem} onClick={handleEditClickWithRef}>
                <EditOutlined />
                {t('chats.editMessage')}
              </div>
              <div className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={handleDeleteClick}>
                <DeleteOutlined />
                {t('chats.deleteMessage.action')}
              </div>
            </>
          )}
        </div>
      )}

      {/* Edit message modal */}
      <Modal
        open={editModalVisible}
        title={t('chats.editMessage')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onOk={handleEditSubmit}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={editMutation.isPending}
      >
        <Input.TextArea
          value={editText}
          onChange={e => setEditText(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 8 }}
          autoFocus
        />
      </Modal>
    </div>
  );
}

const MessagesList = forwardRef(MessagesListInner);
MessagesList.displayName = 'MessagesList';
export default MessagesList;
